<script>
import { computed, defineComponent, onMounted, ref } from "vue";

import TgIconButton from "@/abc-dsk/components/IconButton/TgIconButton.vue";
import ClickOutside from "@/abc-dsk/directives/click-outside";
import { isEscape } from "@/common/event-helpers";

import { CLASS_NAME_COMPONENT, convertToUnit, OriginEnum, PositionEnum } from "./common";

export default defineComponent({
  name: "TgMenu",
  components: { TgIconButton },
  directives: { ClickOutside },
  inheritAttrs: false,
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    buttonLabel: {
      type: String,
      default: undefined
    },
    buttonClass: {
      type: [String, Array, Object],
      default: undefined
    },
    menuClass: {
      type: [String, Array, Object],
      default: undefined
    },
    position: {
      type: String,
      default: PositionEnum.BottomLeft,
      validator: (value) => Object.values(PositionEnum).includes(value)
    },
    origin: {
      type: String,
      default: (props) => props.position.split(" ")[1] ?? OriginEnum.Left,
      validator: (value) => Object.values(OriginEnum).includes(value)
    },
    offsetX: {
      type: Number,
      default: 0
    },
    offsetY: {
      type: Number,
      default: 0
    },
    zIndex: {
      type: Number,
      default: 50
    }
  },
  setup(props, { expose }) {
    const baseClass = CLASS_NAME_COMPONENT;

    const button = ref(null);
    const menu = ref(null);
    const menuId = `tg-menu-${Math.random().toString(36).substring(0, 8)}`;

    // Open state
    const opened = ref(false);
    const toggle = () => {
      if (opened.value === true) {
        close();
      } else {
        open();
      }
    };
    const isOpen = computed(() => opened.value && props.disabled === false);

    // Positioning of the menu
    const getPosition = (el) => ({
      top: Math.round(el?.offsetTop || 0),
      left: Math.round(el?.offsetLeft || 0),
      width: Math.round(el?.getBoundingClientRect().width || 0),
      height: Math.round(el?.getBoundingClientRect().height || 0)
    });
    const getMenuPosition = (buttonPosition) => {
      let offsetY = 0;
      let offsetX = 0;

      switch (props.position) {
        case PositionEnum.BottomLeft:
          offsetY = buttonPosition.height;
          offsetX = 0;
          break;

        case PositionEnum.BottomCenter:
          offsetY = buttonPosition.height;
          offsetX = buttonPosition.width / 2;
          break;

        case PositionEnum.BottomRight:
          offsetY = buttonPosition.height;
          offsetX = buttonPosition.width;
          break;

        default:
          break;
      }

      const menuPosition = getPosition(menu.value);
      switch (props.origin) {
        case OriginEnum.Left:
          offsetX += -menuPosition.width;
          break;

        case OriginEnum.Center:
          offsetX += -(menuPosition.width / 2);
          break;

        case OriginEnum.Right:
          // offsetX should just be the same
          break;

        default:
          break;
      }

      return {
        top: buttonPosition.top + offsetY + props.offsetY,
        left: buttonPosition.left + offsetX + props.offsetX
      };
    };
    const buttonPosition = ref({
      top: 0,
      left: 0,
      width: 0,
      height: 0
    });
    const menuPosition = ref({
      top: 0,
      left: 0
    });
    const updateButtonPosition = () => {
      if (button.value?.$el) {
        buttonPosition.value = getPosition(button.value.$el);
      }
    };
    const updateMenuPosition = () => (menuPosition.value = getMenuPosition(buttonPosition.value));
    const setPositions = () => {
      updateButtonPosition();
      updateMenuPosition();
    };

    // Global window event handlers
    const open = () => {
      opened.value = true;
      setPositions();

      requestAnimationFrame(() => {
        menu.value?.focus();
      });
    };
    const close = (focus = true) => {
      if (focus) {
        button.value?.$el.focus();
      }

      opened.value = false;
    };
    window.addEventListener("resize", () => {
      requestAnimationFrame(setPositions);
    });
    window.addEventListener("keyup", (e) => {
      if (!isOpen.value) {
        return false;
      }

      e.preventDefault();

      // TODO: Put these event helpers in DSK?
      if (isEscape(e)) {
        close();
      }
    });
    const onClickOutside = () => {
      if (isOpen.value === true) {
        close(false);
      }
    };
    const clickOutsideConfig = ref(null);

    // Styles & classes, etc.
    const styles = computed(() => ({
      top: convertToUnit(menuPosition.value.top),
      left: convertToUnit(menuPosition.value.left),
      zIndex: isOpen.value ? props.zIndex : -1
    }));
    const btnClasses = computed(() => [`${baseClass}-button`, props.buttonClass]);
    const menuClasses = computed(() => {
      const classes = [baseClass, props.menuClass];

      if (isOpen.value === true) {
        classes.push(`${baseClass}--open`);
      }

      return classes;
    });

    onMounted(() => {
      setPositions();

      clickOutsideConfig.value = {
        handler: onClickOutside,
        activator: button.value?.$el
      };
    });

    expose({
      open,
      close
    });

    return {
      btnClasses,
      menuClasses,

      button,
      menu,

      isOpen,
      toggle,
      open,
      close,
      clickOutsideConfig,

      menuId,
      styles
    };
  }
});
</script>

<template>
  <TgIconButton
    ref="button"
    :class="btnClasses"
    aria-haspopup="true"
    :aria-label="buttonLabel"
    :aria-expanded="isOpen"
    :aria-controls="menuId"
    :disabled="disabled"
    :state="isOpen ? 'hovered' : undefined"
    @click.prevent="toggle()"
    @keyup.prevent.up="close()"
    @keyup.prevent.down="open()"
  >
    <slot name="icon">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="4"
        height="16"
        viewBox="0 0 4 16"
        role="presentation"
      >
        <path
          d="M2 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2C.9 6 0 6.9 0 8s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
          fill="currentColor"
          fill-rule="evenodd"
        />
      </svg>
    </slot>
  </TgIconButton>
  <div
    v-if="button"
    :id="menuId"
    ref="menu"
    v-click-outside="clickOutsideConfig"
    role="menu"
    :aria-hidden="!isOpen"
    :class="menuClasses"
    :style="styles"
  >
    <slot />
  </div>
</template>
>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.tg-menu-button {
  position: relative;
  width: 34px;
  min-width: 34px;
  height: 34px;
  min-height: 34px;
}

.tg-menu {
  display: inline-block;
  visibility: hidden;
  position: absolute;

  // Just "hide" it, so the JS still can calculate the width/height etc.
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  transition:
    box-shadow 200ms ease-in-out,
    opacity 200ms ease-in-out;
  outline: none;
  opacity: 0;
  contain: content;
  touch-action: none;

  &--open {
    visibility: visible;
    overflow-x: hidden;
    overflow-y: auto;
    clip: unset;
    opacity: 1;
    box-shadow: 6px 6px 20px rgba(0, 0, 0, 0.1);
    touch-action: auto;
  }
}
</style>
